/******/ (() => { // webpackBootstrap
/******/ 	"use strict";

;// ./src/js/Axeptio.js
class Axeptio {
  constructor() {
    this.footerLinksSelector = '.footer .footer__link-item a';
  }

  init = () => {
    const footerLinks = document.querySelectorAll(this.footerLinksSelector);

    if (footerLinks.length === 0) {
      return;
    }

    footerLinks.forEach((link) => {
      if (link.href.includes('#replaceAxeptio')) {
        // eslint-disable-next-line no-script-url
        link.href = 'javascript:openAxeptioCookies()';
      }
    });
  };
}

;// ./src/js/CartUpsell.js
class CartUpsell {
  constructor() {
    this.checkboxSelector = '[data-cart-upsell-checkbox]';
  }

  init = () => {
    // Delegated listener: the cart drawer content is re-rendered on each cart change
    document.addEventListener('change', this.handleChange);
  };

  handleChange = async (event) => {
    const checkbox = event.target.closest(this.checkboxSelector);

    if (!checkbox) {
      return;
    }

    // Prevent concurrent requests (mouse and keyboard) until the cart is updated
    checkbox.disabled = true;
    this.dispatch('theme:loading:start');

    try {
      const cart = await this.updateCart(
        checkbox.dataset.variantId,
        checkbox.checked ? 1 : 0
      );

      if (window.themeVariables?.settings?.pageType === 'cart') {
        window.location.reload();
        return;
      }

      this.restoreFocusAfterRefresh(checkbox);
      this.dispatch('cart:change', { baseEvent: 'line-item:change', cart });
    } catch (error) {
      checkbox.checked = !checkbox.checked;
      document.dispatchEvent(
        new CustomEvent('toast:show', {
          detail: { message: error.message, tone: 'error' },
        })
      );
    } finally {
      this.dispatch('theme:loading:end');
      checkbox.disabled = false;
    }
  };

  updateCart = async (variantId, quantity) => {
    // Let the theme sections (cart drawer...) register themselves to be re-rendered
    const sections = new Set();
    this.dispatch('cart:prepare-bundled-sections', { sections });

    const response = await fetch(
      `${window.Shopify.routes.root}cart/update.js`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          updates: { [variantId]: quantity },
          sections: [...sections].join(','),
        }),
      }
    );
    // Error responses may not be JSON (e.g. 5xx HTML page)
    const cart = await response.json().catch(() => ({}));

    if (!response.ok) {
      throw new Error(cart.description || cart.message || response.statusText);
    }

    return cart;
  };

  // The drawer re-render replaces the checkbox: restore keyboard focus on the new one
  restoreFocusAfterRefresh = (checkbox) => {
    const drawer = checkbox.closest('cart-drawer');
    const focusCheckbox = () => {
      document.getElementById(checkbox.id)?.focus({ preventScroll: true });
    };

    drawer?.addEventListener('cart-drawer:refreshed', focusCheckbox, {
      once: true,
    });
  };

  dispatch = (name, detail = {}) => {
    document.documentElement.dispatchEvent(
      new CustomEvent(name, { bubbles: true, detail })
    );
  };
}

;// ./src/js/ClickAndCollect.js
class ClickAndCollect {
  constructor() {
    this.containerSelector = '[data-click-and-collect]';
    this.stores = [
      { id: 'centre-ville', stockKey: 'stock_centre_ville' },
      { id: 'aushopping', stockKey: 'stock_aushopping' },
      { id: 'stade', stockKey: 'stock_stade' },
    ];
  }

  init = () => {
    this.container = document.querySelector(this.containerSelector);
    const clickAndCollect = window.themeVariables?.webqam?.clickAndCollect;

    if (!this.container || !clickAndCollect?.variants?.length) {
      return;
    }

    this.variantsData = clickAndCollect.variants;
    this.config = this.getConfig();

    const currentVariant = this.getCurrentVariant();

    if (currentVariant) {
      this.updateContainer(currentVariant.id);
    }

    document.addEventListener('variant:change', this.handleVariantChange);
  };

  getConfig = () => {
    const configScript = this.container.querySelector(
      '[data-click-and-collect-config]'
    );

    return configScript ? JSON.parse(configScript.textContent) : null;
  };

  getCurrentVariant = () => {
    const variantScript = document.querySelector(
      'variant-picker script[data-variant]'
    );

    if (!variantScript) {
      return null;
    }

    return JSON.parse(variantScript.textContent);
  };

  handleVariantChange = (event) => {
    const variantId = event.detail?.variant?.id;

    if (!variantId) {
      return;
    }

    this.updateContainer(variantId);
  };

  updateContainer = (variantId) => {
    const variantData = this.variantsData.find(
      (variant) => Number(variant.id) === Number(variantId)
    );

    if (!variantData) {
      return;
    }

    const { available } = variantData;
    const stocks = this.stores.map(({ stockKey }) => variantData[stockKey]);
    const shouldShow = available || stocks.some((stock) => stock > 0);

    this.container.hidden = !shouldShow;

    if (!shouldShow) {
      return;
    }

    this.stores.forEach(({ id, stockKey }) => {
      this.updateStoreStock(id, variantData[stockKey], available);
    });

    const infoLimited = this.container.querySelector(
      '[data-click-and-collect-info-limited]'
    );

    if (infoLimited) {
      infoLimited.hidden = !stocks.some((stock) => stock > 0 && stock <= 10);
    }
  };

  updateStoreStock = (store, stock, variantAvailable) => {
    const storeElement = this.container.querySelector(
      `[data-click-and-collect-store="${store}"]`
    );

    if (!storeElement) {
      return;
    }

    const stockRow = storeElement.querySelector(
      '[data-click-and-collect-store-stock]'
    );
    const clickAndCollectRow = storeElement.querySelector(
      '[data-click-and-collect-store-cnc]'
    );

    if (stockRow && this.config) {
      const isOutOfStock = stock <= 0;
      const isLimited = !isOutOfStock && stock <= 10;

      const swatch = stockRow.querySelector('.click-and-collect__swatch');

      if (swatch) {
        swatch.classList.toggle(
          'click-and-collect__swatch--out-of-stock',
          isOutOfStock
        );
        swatch.classList.toggle(
          'click-and-collect__swatch--limited',
          isLimited
        );
      }

      const textElement = stockRow.querySelector(
        '.click-and-collect__stock-availability-text'
      );

      if (textElement) {
        if (isOutOfStock) {
          textElement.textContent = this.config.outOfStockText;
        } else if (isLimited) {
          textElement.innerHTML = `${this.config.limitedText}<span class="click-and-collect__stock-availability-text-asterisk"> ${this.config.asteriskText}</span>`;
        } else {
          textElement.textContent = this.config.availableText;
        }
      }
    }

    if (clickAndCollectRow) {
      clickAndCollectRow.hidden = !variantAvailable;
    }
  };
}

;// ./src/js/DeliveryDelay.js
class DeliveryDelay {
  constructor() {
    this.estimateDeliverySelector = '.estimate-delivery';
    this.delayMinSelector = '.estimate-delivery__delay--min';
    this.delayMaxSelector = '.estimate-delivery__delay--max';
    this.holidays = [];
  }

  init = async () => {
    const estimateDeliveries = document.querySelectorAll(
      this.estimateDeliverySelector
    );

    if (!estimateDeliveries.length > 0) {
      return;
    }

    const today = new Date();
    const year = today.getFullYear();

    try {
      const response = await fetch(
        `https://calendrier.api.gouv.fr/jours-feries/metropole/${year}.json`
      );
      const data = await response.json();
      this.holidays = Object.keys(data).map((date) => new Date(date));
    } catch (error) {
      console.error('Error loading holidays:', error);
    }

    this.updateDelays();

    document.addEventListener('variant:change', this.updateDelays);
  };

  updateDelays = () => {
    const estimateDeliveries = document.querySelectorAll(
      this.estimateDeliverySelector
    );

    if (!estimateDeliveries.length > 0) {
      return;
    }

    const today = new Date();

    estimateDeliveries.forEach((estimateDelivery) => {
      const delayMin = estimateDelivery.querySelector(this.delayMinSelector);
      const delayMax = estimateDelivery.querySelector(this.delayMaxSelector);

      if (!delayMin || !delayMax) {
        return;
      }

      const delayMinValue = Number.parseInt(delayMin.dataset.delay);
      const delayMaxValue = Number.parseInt(delayMax.dataset.delay);

      const estimatedDateMin = this.addWorkingDays(today, delayMinValue);
      delayMin.innerText = this.formatDate(estimatedDateMin);

      const estimatedDateMax = this.addWorkingDays(today, delayMaxValue);
      delayMax.innerText = this.formatDate(estimatedDateMax);

      estimateDelivery.style.opacity = 1;
    });
  };

  formatDate = (date) => {
    const day = String(date.getDate()).padStart(2, '0');
    const month = date.toLocaleDateString('fr-FR', { month: 'long' });
    const monthFormatted = month.charAt(0).toUpperCase() + month.slice(1);
    return `${day} ${monthFormatted}`;
  };

  isWorkingDay = (date) => {
    const day = date.getDay();
    const isWeekend = day === 0 || day === 6; // Sunday = 0, Saturday = 6
    const isHoliday = this.holidays.some(
      (holiday) => holiday.toDateString() === date.toDateString()
    );
    return !isWeekend && !isHoliday;
  };

  addWorkingDays = (startDate, daysToAdd) => {
    const date = new Date(startDate);
    let addedDays = 0;
    while (addedDays < daysToAdd) {
      date.setDate(date.getDate() + 1);
      if (this.isWorkingDay(date)) {
        addedDays++;
      }
    }
    return date;
  };
}

;// ./src/js/RestockRocketSync.js
class RestockRocketSync {
  constructor() {
    this.variantChangeEvent = 'variant:change';
  }

  init = () => {
    document.addEventListener(
      this.variantChangeEvent,
      this.handleVariantChange
    );
  };

  handleVariantChange = (event) => {
    const variantId = event.detail?.variant?.id;

    if (
      !variantId ||
      typeof window._RestockRocket?.renderButtonForVariant !== 'function'
    ) {
      return;
    }

    window._RestockRocket.renderButtonForVariant(String(variantId));
  };
}

;// ./src/main.js






// JS goes here

window.addEventListener('load', () => {
  const classes = [
    new DeliveryDelay(),
    new Axeptio(),
    new CartUpsell(),
    new ClickAndCollect(),
    new RestockRocketSync(),
  ];
  classes.forEach((classe) => classe.init());
});

/******/ })()
;